import { CircleExclamation } from "@gravity-ui/icons";
import { Flex, Icon, Skeleton, Text } from "@gravity-ui/uikit";
import {
  useCallback,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "react";

import {
  CORNERS,
  DEFAULT_CANVAS_ASPECT_RATIO,
  GUTTER_PX,
  LABELS,
} from "./constants";
import {
  useDeselectOnOutsideClick,
  useImageLoad,
  usePlacementInteraction,
} from "./hooks";
import css from "./ImagePlacementEditor.module.scss";
import { ImagePlacementEditorProps } from "./types";
import { computeGeometry } from "./utils";

export const ImagePlacementEditor = ({
  backgroundUrl,
  overlayUrl,
  positionXPercent,
  positionYPercent,
  sizePercent,
  minSizePercent,
  maxSizePercent,
  onPositionChange,
  onSizeChange,
  emptyHint = LABELS.defaultEmptyHint,
  className,
}: ImagePlacementEditorProps) => {
  // boxRef нужен для outside-click deselect (любой клик в этой зоне =
  // продолжаем работу с overlay'ем). canvasRef — sizing-холст: то, что
  // реально попадёт в выгрузку; всё позиционирование считается от него.
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const bg = useImageLoad(backgroundUrl);
  const overlay = useImageLoad(overlayUrl);

  const [isSelected, setIsSelected] = useState(true);

  const deselect = useCallback(() => setIsSelected(false), []);
  useDeselectOnOutsideClick(boxRef, deselect);

  // Авто-selection при смене overlay'а — handles сразу видны (affordance).
  const [prevOverlayUrl, setPrevOverlayUrl] = useState(overlayUrl);
  if (overlayUrl !== prevOverlayUrl) {
    setPrevOverlayUrl(overlayUrl);
    if (overlayUrl) setIsSelected(true);
  }

  const select = useCallback(() => setIsSelected(true), []);

  const { activeMode, guides, startInteraction, handleMove, endInteraction } =
    usePlacementInteraction({
      containerRef: canvasRef,
      overlayDims: overlay.dims,
      positionXPercent,
      positionYPercent,
      sizePercent,
      minSizePercent,
      maxSizePercent,
      onPositionChange,
      onSizeChange,
      onInteractionStart: select,
    });

  const renderOverlay = () => {
    if (!overlayUrl || overlay.status === "error") return null;
    // Пока подложка не загружена, canvas сидит на fallback aspect-ratio,
    // не совпадающим с реальной картинкой. Drag по нему запишет
    // positionXPercent/Y в чужую систему координат — после загрузки
    // подложки watermark «прыгнет». Не показываем overlay, пока bg
    // не подтвердил dims.
    if (bg.status !== "ok") return null;
    const geom = computeGeometry({
      container: canvasRef.current,
      overlayDims: overlay.dims,
      sizePercent,
      positionXPercent,
      positionYPercent,
    });
    if (!geom) return null;

    return (
      <div
        className={[
          css.overlay,
          isSelected ? css.overlaySelected : "",
          activeMode === "move" ? css.overlayMoving : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          // Overlay позиционируется absolute от .box; canvas сдвинут внутрь
          // на GUTTER_PX, поэтому добавляем gutter-сдвиг к canvas-относительным
          // координатам, которые отдаёт computeGeometry.
          left: GUTTER_PX + geom.leftPx,
          top: GUTTER_PX + geom.topPx,
          width: geom.displayW,
          height: geom.displayH,
        }}
        role="button"
        tabIndex={0}
        aria-label={LABELS.overlayAria}
        aria-pressed={isSelected}
        onPointerDown={(e) => startInteraction("move", e)}
        onPointerMove={handleMove}
        onPointerUp={endInteraction}
        onPointerCancel={endInteraction}
      >
        <img
          src={overlayUrl}
          alt=""
          className={css.overlayImage}
          draggable={false}
        />
        {isSelected &&
          CORNERS.map((corner) => (
            <div
              key={corner}
              className={`${css.handle} ${css[`handle_${corner}`]}`}
              onPointerDown={(e) => startInteraction(corner, e)}
              onPointerMove={handleMove}
              onPointerUp={endInteraction}
              onPointerCancel={endInteraction}
              aria-label={LABELS.resizeHandleAria(corner)}
            />
          ))}
      </div>
    );
  };

  const renderEmptyState = () => {
    if (overlayUrl && overlay.status !== "error") return null;
    return (
      <div className={css.hintPill}>
        <Icon data={CircleExclamation} size={14} />
        <span>
          {overlay.status === "error" ? LABELS.overlayError : emptyHint}
        </span>
      </div>
    );
  };

  // Canvas обязан совпадать по aspect-ratio с реальной подложкой, иначе
  // object-fit:contain даёт letterbox и leftPx/displayW считаются в системе
  // координат канваса, а sharp на бэке — в системе координат картинки.
  // naturalWidth/Height для битых SVG/AVIF может вернуть 0 — это поломает
  // CSS aspect-ratio ("0 / 0" невалидно → canvas схлопывается в 0px), так
  // что валидируем явно.
  const canvasAspectRatio =
    bg.dims && bg.dims.w > 0 && bg.dims.h > 0
      ? `${bg.dims.w} / ${bg.dims.h}`
      : DEFAULT_CANVAS_ASPECT_RATIO;

  // computeGeometry в renderOverlay читает getBoundingClientRect() canvas'а
  // в фазе рендера, поэтому при любом изменении физических размеров canvas'а
  // (смена aspect-ratio после загрузки подложки, viewport resize, font load,
  // resize родителя при сворачивании side-panel) overlay застрянет на
  // старых координатах до следующей interaction. ResizeObserver покрывает
  // все эти кейсы разом и даёт первое сообщение сразу после подписки —
  // initial render тоже корректно перечитывает rect.
  const [, forceRepositionOverlay] = useReducer((n: number) => n + 1, 0);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const observer = new ResizeObserver(() => forceRepositionOverlay());
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={boxRef}
      className={[css.box, className].filter(Boolean).join(" ")}
    >
      {/* canvasInner определяет область экспорта: всё что снаружи него —
          будет обрезано sharp'ом на бэке. overflow: hidden, чтобы фон/гайды
          не вылезли. Overlay рендерим как sibling — он может вылезать. */}
      <div
        ref={canvasRef}
        className={css.canvasInner}
        style={{ aspectRatio: canvasAspectRatio }}
      >
        {bg.status === "ok" && (
          <img
            key={backgroundUrl}
            src={backgroundUrl}
            alt=""
            className={css.bgImage}
            draggable={false}
          />
        )}
        {bg.status === "loading" && <Skeleton className={css.bgSkeleton} />}
        {bg.status === "error" && (
          <Flex
            direction="column"
            gap={2}
            alignItems="center"
            justifyContent="center"
            className={css.bgError}
          >
            <Icon data={CircleExclamation} size={20} />
            <Text variant="body-1" color="danger">
              {LABELS.bgError}
            </Text>
          </Flex>
        )}
        {guides.v && <div className={css.snapGuideV} />}
        {guides.h && <div className={css.snapGuideH} />}
      </div>
      {renderOverlay()}
      {renderEmptyState()}
    </div>
  );
};
